import { ref, computed, watch } from 'vue';

import { isAccommodation, getHotelKey } from '@libs/utils';
import type {
  HotelBookableAndLockableOption,
  DayTicketRate,
  DetailsStaticInfo,
  DetailedDestinationProduct,
  OverviewDestinationStaticInfo,
  DestinationStaticInfo,
  AvailableTransportIngredientResponseWithStaticInfo,
  Dictionary,
  IngredientId,
} from '@libs/trip-planner-interfaces';
import { ProductSearchType } from '@libs/trip-planner-interfaces';
import { isHotelIngredientResponse } from '@libs/search-service-smart-search';

import { useItineraryStore } from '../store/itineraryStore';

import type { RecipeState } from './recipeState';
import type { TripState } from './tripState';

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type, @typescript-eslint/explicit-module-boundary-types
export function setupProductDetailState(
  recipeState: RecipeState,
  tripState: TripState
) {
  const { selectedActivityTickets, getExistingSelectedTickets } = recipeState;
  const { getStaticInfo, hotelIsHearted, isHotelInItinerary } = tripState;
  const itineraryStore = useItineraryStore();

  const destinationProductDetailCode = ref<string>();
  const destinationProductDetailIngredientId = ref<IngredientId>();
  const productSearchType = ref<ProductSearchType | undefined>(undefined);
  const accommodationDetailAvailability = ref<
    Dictionary<HotelBookableAndLockableOption>
  >({});
  const accommodationDetailAvailabilityLoadingCounter = ref<number>(0);
  const activityDetailAvailability = ref<DayTicketRate[]>([]);
  const destinationProductDetailStaticInfo = ref<DetailsStaticInfo>();

  const accommodationDetailAvailabilityLoading = computed<boolean>(
    () => accommodationDetailAvailabilityLoadingCounter.value > 0
  );

  const destinationProductDetail = computed<
    DetailedDestinationProduct | undefined
  >(() => {
    const productCode = destinationProductDetailCode.value;
    const ingredientId = destinationProductDetailIngredientId.value;

    if (!productCode || !ingredientId || !productSearchType.value) {
      return;
    }

    const ingredientResponse =
      itineraryStore.getHotelIngredientResponse(ingredientId) ||
      itineraryStore.getOwnStayIngredientResponse(ingredientId);

    if (!ingredientResponse) {
      return;
    }

    const parentIngredientId = ingredientResponse.ingredientId;
    const staticInfo =
      destinationProductDetailStaticInfo.value?.hotelCode === productCode
        ? destinationProductDetailStaticInfo.value
        : getStaticInfo(productCode, parentIngredientId);

    if (!staticInfo) {
      return;
    }

    const availability =
      productSearchType.value === ProductSearchType.Activity
        ? activityDetailAvailability.value
        : Object.values(accommodationDetailAvailability.value);

    const isHearted =
      isAccommodation(staticInfo.typeOfHotel) &&
      hotelIsHearted(productCode, ingredientId);

    const isInItinerary =
      isAccommodation(staticInfo.typeOfHotel) &&
      isHotelInItinerary(ingredientId, productCode);
    const numberOfNights = ingredientResponse.numberOfNights;
    const isAlternativeProduct =
      productSearchType.value === ProductSearchType.Accommodation
        ? isHotelIngredientResponse(ingredientResponse) &&
          !!ingredientResponse.alternativeSuggestion
        : ingredientResponse.subIngredientResponse.some(
            (subIngredient) =>
              subIngredient.alternativeSuggestion?.code === productCode
          );

    return {
      productCode,
      staticInfo,
      availability,
      isAvailabilityLoading: accommodationDetailAvailabilityLoading.value,
      isHearted,
      isInItinerary,
      numberOfNights,
      parentIngredientId,
      isAlternativeProduct,
    };
  });

  const transportProductDetail = ref<
    AvailableTransportIngredientResponseWithStaticInfo | undefined
  >();
  const shouldScrollToAvailability = ref<boolean>(false);

  watch(destinationProductDetail, () => {
    selectedActivityTickets.value = destinationProductDetail.value
      ? getExistingSelectedTickets(
          destinationProductDetail.value.productCode,
          destinationProductDetail.value.parentIngredientId
        )
      : [];
  });

  function clearDestinationProductDetailAvailability(): void {
    accommodationDetailAvailability.value = {};
    activityDetailAvailability.value = [];
  }

  function setDestinationProductDetailStaticInfo(
    detailsStaticInfo: DetailsStaticInfo
  ): void {
    destinationProductDetailStaticInfo.value = detailsStaticInfo;
    tripState.insertOverviewStaticInfo({
      destinationStaticInfo: {},
      subIngredientsStaticInfo: {},
      heartedHotelsStaticInfo: {
        [getHotelKey(
          detailsStaticInfo.hotelCode,
          detailsStaticInfo.ingredientId
        )]: detailsStaticInfo,
      },
    });
  }

  function clearDestinationProductDetailStaticInfo(): void {
    destinationProductDetailStaticInfo.value = undefined;
  }

  function selectDestinationProduct(
    hotelCode: string,
    ingredientId: IngredientId,
    type: ProductSearchType
  ): void {
    clearTransportProductDetails();
    if (
      destinationProductDetailCode.value === hotelCode &&
      destinationProductDetailIngredientId.value === ingredientId &&
      productSearchType.value === type
    ) {
      return;
    }

    // Refactor what: destinationProductDetail computed
    // Refactor why: side effects are hard to follow and understan
    destinationProductDetailCode.value = hotelCode;
    destinationProductDetailIngredientId.value = ingredientId;
    productSearchType.value = type;
    accommodationDetailAvailability.value = {};
    activityDetailAvailability.value = [];
  }

  function clearDestinationProductDetails(): void {
    destinationProductDetailCode.value = undefined;
    destinationProductDetailIngredientId.value = undefined;
    productSearchType.value = undefined;
    accommodationDetailAvailability.value = {};
    activityDetailAvailability.value = [];
  }

  function isDetailStaticInfo(
    staticInfo:
      | OverviewDestinationStaticInfo
      | DestinationStaticInfo
      | DetailsStaticInfo
  ): staticInfo is DetailsStaticInfo {
    return 'distances' in staticInfo && 'properties' in staticInfo;
  }

  function clearTransportProductDetails(): void {
    transportProductDetail.value = undefined;
  }

  async function clearProductDetails(): Promise<void> {
    clearDestinationProductDetails();
    clearTransportProductDetails();
  }

  return {
    destinationProductDetail,
    transportProductDetail,
    accommodationDetailAvailability,
    activityDetailAvailability,
    accommodationDetailAvailabilityLoading,
    accommodationDetailAvailabilityLoadingCounter,
    destinationProductDetailCode,
    destinationProductDetailIngredientId,
    productSearchType,
    shouldScrollToAvailability,
    setDestinationProductDetailStaticInfo,
    clearDestinationProductDetailAvailability,
    clearDestinationProductDetailStaticInfo,
    clearProductDetails,
    selectDestinationProduct,
    isDetailStaticInfo,
  };
}

export type ProductDetailState = ReturnType<typeof setupProductDetailState>;
